import { Radio, Select } from "antd";
import _ from "lodash";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import { COLOR_DARK, COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const { Group: RadioGroup } = Radio;

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 2px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const OPTION_STATUS = [
  { label: "Windows", value: "WINDOWS" },
  { label: "Android", value: "ANDROID" },
];

export const FormEdit = ({
  setDataList = () => undefined,
  setIsModalVisible,
  setSelectedData,
  dataList,
  floors,
  selectedData,
}) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: _.get(selectedData, ["name"]),
      type: _.get(selectedData, ["type"]),
      width: _.get(selectedData, ["screen_width"]),
      height: _.get(selectedData, ["screen_height"]),
      floor_id: _.get(selectedData, ["floor_id"]),
      description: _.get(selectedData, ["description"]),
      player_id: _.get(selectedData, ["player_id"]),
    },
  });

  const onHandleEdit = async (data) => {
    const name = _.get(data, ["name"]);
    const width = _.get(data, ["width"]);
    const height = _.get(data, ["height"]);
    const type = _.get(data, ["type"]);
    const floor_id = _.get(data, ["floor_id"]);
    const description = _.get(data, ["description"]);
    const player_id = _.get(data, ["player_id"]);
    const id = _.get(selectedData, ["id"]);

    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/device-update",
      isAuth: true,
      data: {
        id,
        name,
        type,
        screen_width: width,
        screen_height: height,
        description,
        player_id,
        floor_id,
      },
    });
    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      const updateData = _.chain(results)
        .thru((item) => {
          const floorId = _.get(item, ["floor_id"]);
          return { ...item, floor_id: _.toInteger(floorId) };
        })
        .value();
      toast.success("Update Device Success.");
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      setSelectedData(null);
      setDataList((prev) => {
        const updateDataList = _.map(prev, (item) => {
          return _.get(item, ["id"]) ===
            _.chain(results).get(["id"]).toInteger().value()
            ? { ...item, ...updateData }
            : item;
        });
        return updateDataList;
      });
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleEdit)} autoComplete="off">
      <Line />
      <InnerContainer style={{ overflow: "scroll" }}>
        <div>
          <Controller
            name="name"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Name
                </Text>
                <StyledInput
                  label="name"
                  {...field}
                  size="large"
                  placeholder="name"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="width"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Width
                </Text>
                <StyledInput
                  label="width"
                  {...field}
                  size="large"
                  placeholder="width"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="height"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Height
                </Text>
                <StyledInput
                  label="height"
                  {...field}
                  size="large"
                  placeholder="height"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="type"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Type
                </Text>
                <RadioGroup
                  {...field}
                  options={OPTION_STATUS}
                  optionType="button"
                  style={{
                    borderColor: error ? "red" : undefined,
                  }}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="floor_id"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Floor
                </Text>
                <Select
                  {...field}
                  size="large"
                  showSearch
                  placeholder="Select a floor"
                  optionFilterProp="children"
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={floors}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="description"
            control={control}
            // rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Description
                </Text>
                <StyledInput
                  label="description"
                  {...field}
                  size="large"
                  placeholder="Description"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="player_id"
            control={control}
            // rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Player ID
                </Text>
                <StyledInput
                  label="player_id"
                  {...field}
                  size="large"
                  placeholder="Player ID"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>

        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
